import React from "react";
import { Card, CardBody, CardTitle, Button, Form, Input, FormFeedback, Spinner, FormGroup } from "reactstrap";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useAuth } from "../../../contexts/AuthContext";
import toast from "react-hot-toast";
import ToastNotification from "components/Toast/ToastNotification";
import slash from "assets/icons/slash-circle.svg";

const LoginForm = ({ onSuccess, haveForgot, haveRegister, onOtpRequired }) => {
  const { onLogin } = useAuth();
  const [showPassword, setShowPassword] = React.useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email("รูปแบบอีเมลไม่ถูกต้อง").required("กรุณากรอกอีเมล"),
      password: Yup.string()
        .required("กรุณากรอกรหัสผ่าน")
        .min(8, "รหัสผ่านต้องมีความยาวอย่างน้อย 8 ตัวอักษร")
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,100}$/, "รหัสผ่านต้องประกอบด้วย ตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษอย่างน้อยอย่างละ 1 ตัว")
        .test("not-weak", "รหัสผ่านนี้ง่ายเกินไป กรุณาใช้รหัสผ่านที่รัดกุมกว่านี้", (value) => {
           const weakPasswords = ["password", "12345678", "123456789", "admin123", "password123", "qwerty", "11111111"];
           return !weakPasswords.includes(value ? value.toLowerCase() : "");
        }),
    }),
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        const result = await onLogin(values);
        if (result?.error) {
          throw result.error;
        } else if (result?.data?.user) {
          if (onSuccess) onSuccess();
        }
      } catch (error) {
        if (error.status === 403) {
          if (onOtpRequired) {
            onOtpRequired({
              email: values.email,
              password: values.password,
            });
          }
        } else {
          let id = toast.custom(<ToastNotification variant="danger" icon={slash} title="เข้าสู่ระบบไม่สำเร็จ" description={error?.data?.message || "เกิดข้อผิดพลาดในการเข้าสู่ระบบ"} onSecondaryClick={() => toast.dismiss(id)} />, { position: "top-right", duration: 5000 });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Card className="card-auth">
      <CardTitle className="text-center">
        <h1 className="text-4xl fw-semibold">เข้าสู่ระบบ</h1>
      </CardTitle>
      <CardBody>
        <Form
          className="w-full"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <h4 className="text-center text-gray-500 mb-3">กรุณากรอกอีเมลที่ใช้สมัครสมาชิก เพื่อทำการเข้าสู่ระบบ</h4>
          <FormGroup>
            <div className="form-horizontal w-full d-flex flex-column gap-4">
              <div>
                <Input
                  //
                  name="email"
                  type="email"
                  className="form-control form-control-lg"
                  placeholder="อีเมล"
                  value={validation.values.email || ""}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={validation.isSubmitting}
                  invalid={validation.touched.email && validation.errors.email}
                />
                {validation.touched.email && validation.errors.email && <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>}
              </div>
              <div>
                <div className="password-input-wrapper">
                  <Input
                    name="password"
                    className="form-control form-control-lg"
                    placeholder="รหัสผ่าน"
                    type={showPassword ? "text" : "password"}
                    value={validation.values.password || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    disabled={validation.isSubmitting}
                    invalid={validation.touched.password && validation.errors.password ? true : false}
                  />
                  <button
                    className="password-toggle-btn"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={validation.isSubmitting}
                  >
                    <i className={showPassword ? "mdi mdi-eye-off-outline" : "mdi mdi-eye-outline"}></i>
                  </button>
                </div>
                {validation.touched.password && validation.errors.password && <FormFeedback type="invalid" className="d-block">{validation.errors.password}</FormFeedback>}
              </div>
            </div>

            {haveForgot(true)}

            <div className="d-grid w-full mt-2">
              <Button type="submit" color="primary" size="lg" disabled={!validation.isValid || validation.isSubmitting} className="py-2">
                {validation.isSubmitting ? (
                  <>
                    <Spinner size="sm" />
                    กำลังเข้าสู่ระบบ...
                  </>
                ) : (
                  "เข้าสู่ระบบ"
                )}
              </Button>
            </div>
          </FormGroup>
        </Form>
        {haveRegister(false)}
      </CardBody>
    </Card>
  );
};

LoginForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onOtpRequired: PropTypes.func.isRequired,
  haveForgot: PropTypes.func.isRequired,
  haveRegister: PropTypes.func.isRequired,
};

export default LoginForm;
